/**
 * Which of the two instruments this is going to be.
 *
 * Asked before any board is shown, because the kind fixes the whole symbol
 * vocabulary and cannot be changed afterwards — a genogram's parent-and-child
 * structure has no meaning on an ecomap. Better a question than a board the
 * therapist has to abandon.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Network, Share2 } from "lucide-react";

import { Modal } from "../../../../shared/ui/modal";
import type { MapKind } from "../../domain/types";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onChoose: (kind: MapKind) => Promise<void> | void;
    /** Named so the therapist can see whose map they are about to create. */
    subjectLabel?: string;
    error?: string | null;
}

export default function MapKindChooser({
    isOpen,
    onClose,
    onChoose,
    subjectLabel,
    error,
}: Props) {
    const { t } = useTranslation();
    const [pending, setPending] = useState<MapKind | null>(null);

    const choose = async (kind: MapKind) => {
        if (pending) return;
        setPending(kind);
        try {
            await onChoose(kind);
        } finally {
            setPending(null);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel={t("systemicMap.kind.choose")}
            className="w-full max-w-lg rounded-2xl bg-surface-overlay p-6"
        >
            <h2 className="text-xl font-semibold text-content-primary">
                {t("systemicMap.kind.choose")}
            </h2>
            {subjectLabel && (
                <p className="mt-1 text-sm text-content-secondary">
                    {t("systemicMap.dock.forPatient", { patient: subjectLabel })}
                </p>
            )}

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <KindCard
                    icon={<Network size={20} aria-hidden="true" />}
                    title={t("systemicMap.kind.genogram")}
                    hint={t("systemicMap.kind.genogramHint")}
                    busy={pending === "genogram"}
                    disabled={pending !== null}
                    onClick={() => choose("genogram")}
                />
                <KindCard
                    icon={<Share2 size={20} aria-hidden="true" />}
                    title={t("systemicMap.kind.ecomap")}
                    hint={t("systemicMap.kind.ecomapHint")}
                    busy={pending === "ecomap"}
                    disabled={pending !== null}
                    onClick={() => choose("ecomap")}
                />
            </div>

            {error && (
                <p role="alert" className="mt-4 text-sm text-status-critical">
                    {error}
                </p>
            )}
        </Modal>
    );
}

function KindCard({
    icon,
    title,
    hint,
    busy,
    disabled,
    onClick,
}: {
    icon: React.ReactNode;
    title: string;
    hint: string;
    busy: boolean;
    disabled: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className="flex min-h-11 flex-col items-start gap-2 rounded-xl border border-border-subtle bg-surface-raised p-5 text-left transition-colors duration-fast hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
            <span className="flex items-center gap-2 text-accent-text">
                {busy ? (
                    <Loader2
                        size={20}
                        aria-hidden="true"
                        className="animate-spin motion-reduce:animate-none"
                    />
                ) : (
                    icon
                )}
                <span className="text-base font-semibold text-content-primary">
                    {title}
                </span>
            </span>
            <span className="text-sm text-content-secondary">{hint}</span>
        </button>
    );
}
